import { useState } from "react";
import { PageHeader } from "@/components/base/PageHeader";
import { SettingsNav } from "@/pages/settings/components/SettingsNav";
import { getPreferences, savePreferences, type AppPreferences } from "@/lib/preferences";

const inputCls =
  "px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";

function Toggle({ checked, onChange, id }: { checked: boolean; onChange: (v: boolean) => void; id: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      id={id}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${checked ? "bg-emerald-600" : "bg-slate-200"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`}
      />
    </button>
  );
}

export default function PreferencesSettings() {
  const [prefs, setPrefs] = useState<AppPreferences>(getPreferences());
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  const update = <K extends keyof AppPreferences>(key: K, value: AppPreferences[K]) => {
    setPrefs((p) => ({ ...p, [key]: value }));
    setSaved(false);
    setDirty(true);
  };

  const handleSave = () => {
    savePreferences(prefs);
    setDirty(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div>
      <PageHeader title="Preferences" subtitle="Personalise your Tradivo experience" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Notification preferences */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-800 mb-1">Notification preferences</h3>
            <p className="text-xs text-slate-500 mb-5">Choose which operational alerts appear in your notification bell and the notifications inbox.</p>
            <div className="space-y-4">
              {([
                { key: "lowStockAlerts" as const, label: "Low stock alerts", desc: "Notify when product stock falls below the reorder point." },
                { key: "overduePoAlerts" as const, label: "Overdue purchase order alerts", desc: "Notify when an expected delivery date has passed." },
                { key: "pendingReturnAlerts" as const, label: "Returns pending inspection", desc: "Notify when a return is received and awaiting disposition." },
                { key: "readyToShipAlerts" as const, label: "Ready to ship alerts", desc: "Notify when fulfilled orders are waiting for dispatch." },
              ] as const).map(({ key, label, desc }) => (
                <div key={key} className="flex items-center justify-between gap-4 py-2 border-b border-slate-50 last:border-0">
                  <div>
                    <label htmlFor={`toggle-${key}`} className="text-sm font-medium text-slate-800 cursor-pointer">{label}</label>
                    <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                  </div>
                  <Toggle checked={prefs[key]} onChange={(v) => update(key, v)} id={`toggle-${key}`} />
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-4">
              These preferences are stored locally in this browser and control which alerts appear in your bell and inbox.
            </p>
          </div>

          {/* Display */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-800 mb-1">Display</h3>
            <p className="text-xs text-slate-500 mb-5">Adjust how dates are displayed across the platform.</p>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">Date format</label>
              <select
                value={prefs.dateFormat}
                onChange={(e) => update("dateFormat", e.target.value as AppPreferences["dateFormat"])}
                className={inputCls}
              >
                <option value="dmy">DD/MM/YYYY (Australia, UK, Europe)</option>
                <option value="mdy">MM/DD/YYYY (US)</option>
                <option value="ymd">YYYY-MM-DD (ISO 8601)</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            {dirty && (
              <span className="flex items-center text-sm text-amber-600 gap-1.5">
                <i className="ri-error-warning-line" /> Unsaved changes
              </span>
            )}
            {saved && (
              <span className="flex items-center text-sm text-emerald-600 gap-1.5">
                <i className="ri-check-line" /> Saved
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={!dirty}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              Save preferences
            </button>
          </div>
        </div>
        <SettingsNav />
      </div>
    </div>
  );
}